import { useCallback } from 'react';
import { KeyedMutator } from 'swr';

import { coreApi } from '@/api';
import { toast } from '@/utils';
import {
  createNewMission,
  extractQuestResponseData,
  MISSION_API_VERSION_HEADER
} from '@/pages/missions/utils';
import { conditionType, generateRandomKey } from '@/components/ActionHandler/utils';

interface ConnectQuestParams {
  sourceQuestId: number;
  commandType: string;
  targetKey: 'missionId' | 'questId';
  targetId: number;
  missions?: Mission[];
}

function findQuestById(missions: Mission[] | undefined, questId: number): Quest | undefined {
  if (!missions) return undefined;
  for (const mission of missions) {
    const quest = mission.quests?.find((candidate) => candidate.id === questId);
    if (quest) return quest;
  }
  return undefined;
}

export default function useFlowActions(
  appId: string,
  scenarioId: string,
  mutate: KeyedMutator<Mission[]>
) {
  const addMission = useCallback(
    async (order?: number) => {
      const mission = await createNewMission({ appId, scenarioId, order });
      await mutate();
      return mission;
    },
    [appId, scenarioId, mutate]
  );

  const deleteMission = useCallback(
    async (missionId: number) => {
      await coreApi.delete(`/apps/${appId}/missions/${missionId}`, MISSION_API_VERSION_HEADER);
      await mutate();
      toast({ message: '미션을 삭제했어요' });
    },
    [appId, mutate]
  );

  const addQuest = useCallback(
    async (missionId: number) => {
      const { data } = await coreApi.post<Quest | CommonResponseModel<Quest>>(
        `/apps/${appId}/missions/${missionId}/quests`,
        {
          name: '새 퀘스트',
          description: '',
          label: '새 퀘스트',
          isVisibleDefault: true,
          isLockedDefault: false
        },
        MISSION_API_VERSION_HEADER
      );
      await mutate();
      const createdQuest = extractQuestResponseData(data);
      if (!createdQuest) {
        toast({ message: '생성된 퀘스트 정보를 불러오지 못했어요' });
      }

      return createdQuest;
    },
    [appId, mutate]
  );

  const deleteQuest = useCallback(
    async (questId: number) => {
      await coreApi.delete(`/apps/${appId}/quests/${questId}`, MISSION_API_VERSION_HEADER);
      await mutate();
      toast({ message: '퀘스트를 삭제했어요' });
    },
    [appId, mutate]
  );

  const connectQuest = useCallback(
    async ({ sourceQuestId, commandType, targetKey, targetId, missions }: ConnectQuestParams) => {
      const sourceQuest = findQuestById(missions, sourceQuestId);
      if (!sourceQuest) {
        toast({ message: '연결할 퀘스트를 찾지 못했어요' });
        return;
      }

      const actionHandler = sourceQuest.actionHandler ?? {
        actionType: 'RealWorld.ActionTypes.Dummy',
        actionParameters: {},
        reactionDefinitions: [],
        failureCommands: []
      };

      const reactionDefinitions = [...(actionHandler.reactionDefinitions ?? [])];
      const hasSameCommand = reactionDefinitions.some((definition) =>
        (definition.commands ?? []).some(
          (command) =>
            command.type === commandType && Number(command.parameters?.[targetKey]) === targetId
        )
      );

      if (hasSameCommand) {
        toast({ message: '이미 연결된 노드예요' });
        return;
      }

      const newCommand: ReactionCommand = {
        key: generateRandomKey(),
        type: commandType,
        parameters: {
          [targetKey]: targetId
        }
      };

      if (reactionDefinitions.length === 0) {
        reactionDefinitions.push({
          key: generateRandomKey(),
          conditions: [
            {
              key: generateRandomKey(),
              type: conditionType.none,
              parameters: {}
            }
          ],
          commands: [newCommand]
        });
      } else {
        const [firstDefinition, ...restDefinitions] = reactionDefinitions;
        reactionDefinitions.splice(
          0,
          reactionDefinitions.length,
          {
            ...firstDefinition,
            commands: [...(firstDefinition.commands ?? []), newCommand]
          },
          ...restDefinitions
        );
      }

      await coreApi.put(
        `/apps/${appId}/quests/${sourceQuestId}/action`,
        {
          ...actionHandler,
          reactionDefinitions,
          failureCommands: actionHandler.failureCommands ?? []
        },
        MISSION_API_VERSION_HEADER
      );

      await mutate();
      toast({ message: '연결을 저장했어요' });
    },
    [appId, mutate]
  );

  const connectQuestToMission = useCallback(
    async (sourceQuestId: number, targetMissionId: number, missions?: Mission[]) => {
      await connectQuest({
        sourceQuestId,
        commandType: 'RealWorld.Commands.GotoMission',
        targetKey: 'missionId',
        targetId: targetMissionId,
        missions
      });
    },
    [connectQuest]
  );

  const connectQuestToQuest = useCallback(
    async (sourceQuestId: number, targetQuestId: number, missions?: Mission[]) => {
      await connectQuest({
        sourceQuestId,
        commandType: 'RealWorld.Commands.UnlockQuest',
        targetKey: 'questId',
        targetId: targetQuestId,
        missions
      });
    },
    [connectQuest]
  );

  return {
    addMission,
    deleteMission,
    addQuest,
    deleteQuest,
    connectQuestToMission,
    connectQuestToQuest
  };
}
